import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AppView, Button, ButtonGroup, CardView, NavigationHeader, Spacer } from 'design-system';
import BottomPanel from 'design-system/BottomPanel';
import { HEIGHT, HEIGHT_PLUS_PADDING } from 'design-system/Button';
import ListEmptyView from 'design-system/ListEmptyView';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { SelectableAccountCard } from 'design-system/SelectableAccountCard';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import RowAccount from 'features/accounts/components/RowAccount';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useCreateInvestAccount } from 'features/invest/hooks/useCreateInvestAccount';
import { selectActiveTradingAccountId } from 'features/invest/reducers/selectors';
import { InternalTransfer } from 'features/transfers/types';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { clearOnboardingState } from 'actions/invest';
import { selectGBAccountsWithPISPInfo } from 'reducers/selectors';
import Amplitude from 'utils/amplitude';
import createStyleSheets from 'utils/createStyleSheets';
import Modal from 'utils/packages/Modal';
import { OnboardingFlow } from 'utils/types/navigation';
import { isEmmaDev } from '../../../constants';
const strings = {
    title: 'Select your primary account',
    subTitle: 'Choose the current account your regular income goes into. This will be used for all future deposits and withdrawals',
    notSupported: 'Not supported',
    connectAnotherAccount: 'Connect another account',
    dontSeeAccount: 'Don’t see your primary account?',
    continue: 'Continue',
};
const SelectFundingAccountScreen = ({ route, navigation }) => {
    const dispatch = useAppDispatch();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const isPotsOnboarding = route.params && 'isPotsOnboarding' in route.params ? route.params?.isPotsOnboarding : false;
    const mountedOnAnotherScreen = route.params && 'mountedOnAnotherScreen' in route.params ? route.params.mountedOnAnotherScreen : false;
    const pot = route.params && 'pot' in route.params ? route.params.pot : undefined;
    const { supported, notSupported } = useAppSelector(selectGBAccountsWithPISPInfo);
    const hasSupportedAccounts = !!supported.length;
    const recommendedAccountId = isEmmaDev
        ? supported.find((account) => account.hasSalary)?.id || supported?.[0]?.id
        : supported.find((account) => account.hasSalary)?.id;
    const onboardingState = useAppSelector((state) => state.invest.onboarding);
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const scrollY = useSharedValue(0);
    const [selectedAccountId, setSelectedAccountId] = useState(recommendedAccountId);
    const { paddingHorizontalStyle } = useAppFrameDimensions(mountedOnAnotherScreen);
    const contentContainerStyle = useMarginBottom('paddingBottom', 
    // eslint-disable-next-line no-nested-ternary
    !hasSupportedAccounts
        ? HEIGHT_PLUS_PADDING
        : recommendedAccountId
            ? HEIGHT_PLUS_PADDING + HEIGHT + rem(8)
            : undefined, paddingHorizontalStyle);
    const { submit } = useCreateInvestAccount();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        if ((isPotsOnboarding || (!tradingAccountId && onboardingState)) && !mountedOnAnotherScreen) {
            Amplitude.logEvent('Select Funding Account Screen', { source: isPotsOnboarding ? 'save' : 'invest' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const onConnectAccount = useCallback(() => {
        navigation.navigate('ChooseBank', { back: true, investments: false });
    }, [navigation]);
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y;
    });
    const handleSelectAccount = useCallback((account) => {
        if (pot) {
            navigation.navigate('Transfer', {
                to: pot,
                type: InternalTransfer.fund,
                from: { ...account, itemType: 'investConnectedBankAccount' },
            });
        }
        else if (isPotsOnboarding) {
            navigation.navigate('AutosaveSelectFundingAccount', {
                account,
            });
        }
        else if (route.params && 'rules' in route.params) {
            // autosave onboarding
            navigation.navigate('AutosaveSelectBank', {
                bankAccount: account,
                rules: route.params.rules,
                overdraft: route.params.overdraft,
                distribution: route.params.distribution,
                collectionFrequency: route.params.collectionFrequency,
            });
        }
        else if (!tradingAccountId) {
            if (onboardingState) {
                if (onboardingState?.hasJISAOrTrustFund) {
                    submit(account.id);
                    return;
                }
                if (onboardingState.accountType && onboardingState.assetId) {
                    navigation.navigate('AutoinvestSelectFundingAccount', {
                        accountType: onboardingState.accountType,
                        collectionDate: onboardingState.autoinvestDayOfMonth || 1,
                        amount: onboardingState.amount?.monthly || 0,
                        allocations: [
                            {
                                assetId: onboardingState.assetId,
                                percentage: 100,
                            },
                        ],
                        account,
                    });
                }
            }
            else {
                dispatch(clearOnboardingState());
                navigation.popToTop();
            }
        }
    }, [isPotsOnboarding, onboardingState, pot, route.params, submit, tradingAccountId]);
    const onSelectAccount = useCallback((account) => () => {
        setSelectedAccountId(account.id);
    }, []);
    const handleContinue = useCallback(() => {
        const account = supported.find((item) => item.id === selectedAccountId);
        if (account) {
            if (account.hasSalary) {
                handleSelectAccount(account);
            }
            else {
                Modal.showConfirmation({
                    title: 'Are you sure you want to pick a different account?',
                    subtitle: 'We recommend the current account your regular income goes into, so deposits and withdrawals stay simple',
                    confirmButtonTitle: 'Continue',
                    cancelButtonTitle: 'Reselect',
                    onConfirm: () => {
                        handleSelectAccount(account);
                    },
                });
            }
        }
    }, [handleSelectAccount, selectedAccountId, supported]);
    const BottomButtons = useMemo(() => {
        if (!hasSupportedAccounts) {
            return (<ButtonGroup type="floating" isInTab={!!mountedOnAnotherScreen}>
          <Button brand onPress={onConnectAccount} title={strings.connectAnotherAccount}/>
        </ButtonGroup>);
        }
        if (recommendedAccountId) {
            return (<ButtonGroup type="floating" isInTab={!!mountedOnAnotherScreen}>
          <Button brandReversed onPress={onConnectAccount} title={strings.connectAnotherAccount}/>
          <Button brand title={strings.continue} onPress={handleContinue} disabled={selectedAccountId === undefined}/>
        </ButtonGroup>);
        }
        return (<BottomPanel isInTab={!!mountedOnAnotherScreen}>
        <Text TextThin-16 Secondary centered>
          {strings.dontSeeAccount}
        </Text>
        <Spacer h={16}/>
        <Button brandReversed onPress={onConnectAccount} title={strings.connectAnotherAccount}/>
        <Spacer h={8}/>
        <Button brand disabled onPress={handleContinue} title={strings.continue}/>
      </BottomPanel>);
    }, [
        handleContinue,
        hasSupportedAccounts,
        recommendedAccountId,
        mountedOnAnotherScreen,
        onConnectAccount,
        selectedAccountId,
    ]);
    return (<AppView type="secondary" withFrame={false}>
      <NavigationHeader scrollY={scrollY} hideTitleOnScroll regularTitleHeight outputColors={headerColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? (<LeaveOnboardingButton flow={isPotsOnboarding ? OnboardingFlow.Pots : OnboardingFlow.Invest}/>) : undefined} title="" rightContent={<View style={mountedOnAnotherScreen ? styles.headerRight : null}>
            <HelpScreenButton articlesKey={
            // eslint-disable-next-line no-nested-ternary
            pot
                ? HELP_ARTICLES_KEY.POTS_ONBOARDING
                : route.params && 'rules' in route.params
                    ? HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING
                    : undefined}/>
          </View>}/>
      <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title={strings.title} subTitle={strings.subTitle}/>
        <CardView noMarginTop>
          {supported.map((item) => (<SelectableAccountCard data={item} key={item.id} type="single" preTitle={item.hasSalary ? (<Text Text-12 Primary>
                    RECOMMENDED
                  </Text>) : null} hideSpaceUserAvatar hideActionRequiredText style={styles.rowAccount} isSelected={selectedAccountId === item.id} onPress={onSelectAccount(item)}/>))}
          {!supported.length &&
            notSupported.map((item) => (<View style={styles.disabledCard} key={item.id}>
                <RowAccount disabled data={item} primaryBalanceOnly hideSpaceUserAvatar style={styles.rowAccount} hideActionRequiredText notSupportedText={strings.notSupported}/>
              </View>))}
          {!supported.length && !notSupported.length && (<ListEmptyView style={styles.listEmptyView} title="There are no accounts"/>)}
        </CardView>
      </Animated.ScrollView>
      {BottomButtons}
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    rowAccount: {
        paddingHorizontal: rem(16),
    },
    disabledCard: {
        opacity: 0.5,
    },
    headerRight: {
        marginBottom: rem(6),
        paddingRight: rem(16),
        alignItems: 'flex-end',
    },
    listEmptyView: {
        paddingVertical: rem(24),
    },
}));
export default SelectFundingAccountScreen;
